<template>
    <div class="infinite-carousel" ref="container">
        <div 
            class="scroll-content" 
            ref="content" 
            :style="{ transform: `translateY(${scrollPosition}px)` }"
            @mouseenter="handleMouseEnter" 
            @mouseleave="handleMouseLeave"
        >
            <div class="original-items">
                <slot></slot>
            </div>
            <div v-show="hasMultipleItems" class="cloned-items">
                <slot></slot>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'InfiniteCarousel',
    props: {
        speed: {
            type: Number,
            default: 1
        },
        autoScroll: {
            type: Boolean,
            default: true
        },
        itemHeight: {
            type: Number,
            default: 72 // Default height of v-list-item
        },
        visibleItems: {
            type: Number,
            default: 5
        }
    },
    data() {
        return {
            scrollPosition: 0,
            contentHeight: 0,
            animationFrame: null,
            isPaused: false,
            hasMultipleItems: false,
            scrollSpeed: 0.5, // Base scroll speed
            isInitialized: false,
            initTimer: null
        }
    },
    methods: {
        checkContent() {
            const content = this.$refs.content
            const list = content?.querySelector('.original-items .v-list')
            
            if (list?.children.length) {
                this.isInitialized = true
                clearTimeout(this.initTimer)
                this.initCarousel()
            } else {
                // Keep checking until content is available
                this.initTimer = setTimeout(() => this.checkContent(), 100)
            }
        },

        initCarousel() {
            if (!this.isInitialized) return

            const content = this.$refs.content
            const container = this.$refs.container
            const list = content?.querySelector('.original-items .v-list')
            
            if (!content || !container || !list) return

            const totalItems = list.children.length
            this.hasMultipleItems = totalItems > 1

            // Calculate heights

            // Set container height
           

            // Start animation if needed
            if (this.hasMultipleItems && this.autoScroll) {
                this.startAnimation()
            }
        },

        animate() {
            if (!this.hasMultipleItems || !this.autoScroll || this.isPaused) {
                this.animationFrame = requestAnimationFrame(this.animate)
                return
            }

            this.scrollPosition -= this.speed * this.scrollSpeed

            if (Math.abs(this.scrollPosition) >= this.contentHeight) {
                // Smooth reset
                this.scrollPosition = 0
            }

            this.animationFrame = requestAnimationFrame(this.animate)
        },

        handleMouseEnter() {
            this.isPaused = true
        },

        handleMouseLeave() {
            if (this.hasMultipleItems && this.autoScroll) {
                this.isPaused = false
            }
        },

        startAnimation() {
            this.stopAnimation()
            this.isPaused = false
            this.scrollPosition = 0
            this.animationFrame = requestAnimationFrame(this.animate)
        },

        stopAnimation() {
            if (this.animationFrame) {
                cancelAnimationFrame(this.animationFrame)
                this.animationFrame = null
            }
        },

        pause() {
            if (this.hasMultipleItems) {
                this.isPaused = true
            }
        },

        resume() {
            if (this.hasMultipleItems) {
                this.isPaused = false
            }
        }
    },

    watch: {
        autoScroll: {
            immediate: true,
            handler(newValue) {
                this.$nextTick(() => {
                    if (this.isInitialized) {
                        if (newValue && this.hasMultipleItems) {
                            this.startAnimation()
                        } else {
                            this.stopAnimation()
                        }
                    }
                })
            }
        }
    },

    mounted() {
        this.$nextTick(() => {
            this.checkContent()
            window.addEventListener('resize', this.initCarousel)
        })
    },

    beforeDestroy() {
        clearTimeout(this.initTimer)
        this.stopAnimation()
        window.removeEventListener('resize', this.initCarousel)
    }
}
</script>

<style scoped>
.infinite-carousel {
    overflow: hidden;
    position: relative;
    min-height: 120px;
    /* Minimum height */
}

.scroll-content {
    will-change: transform;
    position: relative;
    transition: transform 0.3s ease-out;
}

.original-items,
.cloned-items {
    padding: 4px 0;
}

.cloned-items {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
}
</style>